import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Grid, Theme, useTheme } from '@mui/material';
import {
  BodyText,
  Heading,
  MinorHeading
} from '../../components/shared/typography/PrimaryTypography';
import { File } from '@esg/esg-global-types';
import { UserContext } from '../../context/UserContext';
import AccessDenied from '../shared/AccessDenied';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';
import ExportTypeSelect, { ExportType } from '../../components/data_export/ExportTypeSelect';
import ExportDataGrid, {
  ExportDataGridRow
} from '../../components/data_export/data_grid/ExportDataGrid';
import ExportCompletedNotification from '../../components/data_export/ExportCompletedNotification';
import { downloadFileFromSignedUrl } from '../../lib/files/file';
import { GCP_FILE_UPLOAD_BUCKET } from '../../env';
import moment from 'moment';
import { useSubscriptionContext } from '../../context/SubscriptionContext';
import AlertBanner from '../../components/shared/banner/AlertBanner';
import { isExportQuotaExempt, type ExceededQuotaKey } from '../../lib/app/subscription';

/**
 * View for exporting data into Syntiro of various entities from various sources.
 * @returns {JSX.Element}
 */
const DataExport = () => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const { subscription, getExceededSubscriptionKeys } = useSubscriptionContext();

  // Track module duration.
  useModuleDurationTracking('Exports', 'N/A', group?.id, company?.id);

  const [access, setAccess] = useState<ViewAccess>(null);
  const [exportType, setExportType] = useState<ExportType | null>(null);
  const [showExportNotification, setShowExportNotification] = useState<boolean>(false);
  const [latestCompletedRow, setLatestCompletedRow] = useState<ExportDataGridRow | null>(null);
  const [showQuotaExceededAlert, setShowQuotaExceededAlert] = useState<boolean>(false);
  const theme: Theme = useTheme();

  const isExempt: boolean = !!user && isExportQuotaExempt(user);
  const exceededSubscriptionKeys: ExceededQuotaKey[] = getExceededSubscriptionKeys([
    'monthly_data_exports'
  ]);

  useEffect(() => {
    if (!isExempt && exceededSubscriptionKeys.length) {
      setShowQuotaExceededAlert(true);
    }
  }, [isExempt, exceededSubscriptionKeys.length, subscription]);

  const handleCloseQuotaAlert = () => {
    setShowQuotaExceededAlert(false);
  };

  useEffect(() => {
    setAccess(
      group && company && user
        ? getViewAccess(user, group.id, company.id, 'exports.export_data')
        : null
    );

    // Reset form flow on group / company change.
    setExportType(null);
  }, [user, group, company]);

  const handleExportNotification = useCallback((latest_row: ExportDataGridRow | null) => {
    setLatestCompletedRow(latest_row);
    setShowExportNotification(true);
  }, []);

  const handleHideExportNotification = () => {
    setShowExportNotification(false);
  };

  const handleExportDownload = async (row: ExportDataGridRow | null) => {
    if (
      row &&
      'completed' in row.metadata &&
      'file_uri' in row.metadata &&
      typeof row.metadata.file_uri === 'string'
    ) {
      const file_path: string | undefined = row.metadata.file_uri;
      const file_name: string | undefined =
        `${row.metadata.key}_${moment(row.metadata.completed).format('YYYY-MM-DD_HH-mm-SS')}`;
      const file: File = {
        id: row.id,
        deleted: null,
        name: file_name ?? file_path,
        type: row.metadata.key,
        uploaded: new Date(row.metadata.completed),
        file_path: row.metadata.file_uri
      };
      await downloadFileFromSignedUrl(atob(GCP_FILE_UPLOAD_BUCKET), file.file_path);
    }
  };

  return !user || access === null ? (
    <LoadingScreen />
  ) : access !== 'none' ? (
    <Box>
      <AlertBanner
        severity="warning"
        message={`Quota reached for ${exceededSubscriptionKeys
          .map((item) => `${item.label} (${subscription?.quota[item.key]})`)
          .join(', ')} allocated to your subscription.`}
        open={showQuotaExceededAlert && !!exceededSubscriptionKeys.length}
        action={handleCloseQuotaAlert}
      />
      <Box
        sx={{
          textAlign: 'start',
          my: 3,
          mx: -3,
          boxSizing: 'border-box',
          borderBottom: `1px solid ${theme.palette.teal.dark}`
        }}
      >
        <Grid container spacing={2} sx={{ px: 4, mb: 4 }}>
          <Grid size={9} alignSelf={'center'}>
            <Heading weight="Medium">Export your data from Syntiro</Heading>
          </Grid>
          <Grid size={12} alignSelf={'center'} display={'flex'}>
            <BodyText>
              Explore all of your Syntiro module data in flexible file formats, get started by
              selecting a common export preset or choose a specific export type below.
            </BodyText>
          </Grid>
        </Grid>
        {/* TODO: Add Export Presets as part of future ticket. */}
      </Box>
      <Grid container spacing={4}>
        <Grid size={6}>
          <Box sx={{ maxWidth: 600 }}>
            <ExportTypeSelect
              selected_options={exportType}
              handleChangeExportType={(new_type: ExportType | null) => setExportType(new_type)}
            />
          </Box>
        </Grid>
        <Grid size={1}></Grid>
        <Grid size={5}>
          {showExportNotification && (
            <ExportCompletedNotification
              latest_completed_row={latestCompletedRow}
              handleExportDownload={handleExportDownload}
            />
          )}
        </Grid>
        {exportType && (
          <Grid size={12} sx={{ textAlign: 'left' }}>
            <MinorHeading weight="Medium">
              Add the parameters for {exportType.name} export
            </MinorHeading>
          </Grid>
        )}
        {exportType?.displayParameters && exportType?.displayParameters(exportType)}
        <Grid
          size={12}
          sx={{
            textAlign: 'start'
          }}
        >
          <MinorHeading weight="Medium" sx={{ mb: 2 }}>
            Recent Data Exports
          </MinorHeading>
          <ExportDataGrid
            handleExportNotification={handleExportNotification}
            handleExportDownload={handleExportDownload}
            handleHideExportNotification={handleHideExportNotification}
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <AccessDenied />
  );
};

export default DataExport;
